<template>
    <div class="grid">
        <div class="unit whole">
            <h1 v-if="teacher">Welcome, {{ teacher.name }}</h1>
            <template v-if="school">
                <template v-if="school.bypass_marking"
                    ><v-alert type="info">Online marking is switched off for this school.</v-alert></template
                >
                <template v-else-if="taskStats && (taskStats.ready_for_marking || taskStats.overdue_tasks)">
                    <v-alert type="info" class="">
                        <div>
                            {{ taskStats.ready_for_marking ? taskStats.ready_for_marking : 'No' }} task{{
                                taskStats.ready_for_marking == 1 ? '' : 's'
                            }}
                            {{ taskStats.ready_for_marking == 1 ? 'has' : 'have' }} been completed and
                            {{ taskStats.ready_for_marking == 1 ? 'is' : 'are' }} ready for
                            <span v-if="taskStats.overdue_tasks == 0">marking.</span>
                            <span v-else>
                                marking and {{ taskStats.overdue_tasks ? taskStats.overdue_tasks : 'no' }} task{{
                                    taskStats.overdue_tasks == 1 ? '' : 's'
                                }}
                                {{ taskStats.overdue_tasks == 1 ? 'is' : 'are' }} overdue.</span
                            >
                        </div>
                        <div class="d-flex justify-end">
                            <v-btn to="/teacher/marking?status=submitted" variant="outlined">Start marking</v-btn>
                        </div>
                    </v-alert>
                </template>
            </template>
        </div>

        <div v-if="school?.type.abbrv == 'WS'" class="unit one-quarter">
            <RouterLink to="/teacher/about-kidsout"
                ><img id="kidsout_frontpage_img" :src="`/common/img/kidsout_logo_transparent.png`"
            /></RouterLink>
        </div>
    </div>

    <div v-if="school?.type.abbrv == 'PREM' && notices.length > 0" class="grid">
        <div class="unit whole">
            <div class="panel">
                <v-carousel
                    height="400px"
                    :show-arrows="notices.length > 1"
                    :hide-delimiters="notices.length < 2"
                    hide-delimiter-background
                    color="primary"
                    cycle
                >
                    <TeacherNoticeBoardItem v-for="notice of notices" :key="`notice-${notice.id}`" :notice="notice" />
                </v-carousel>
            </div>
        </div>
    </div>

    <div v-if="school?.type.abbrv == 'PREM' && featuredContentRegistryBooks.length > 0" class="grid">
        <div class="unit whole">
            <div class="grid">
                <div class="unit whole">
                    <div class="panel">
                        <RouterLink class="btn pull-right" to="/teacher/books">Browse library</RouterLink>
                        <h2>Featured books</h2>
                        <div class="grid no-gutters carousel">
                            <template v-for="(book, i) in featuredContentRegistryBooks" :key="book.id">
                                <div class="unit one-quarter book cursor-pointer" @click="gotoBook(book)">
                                    <TeacherBookCard :book="book" />
                                </div>
                                <br v-if="i > 0 && i % 4 == 3" style="clear: both" />
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="school?.type.abbrv == 'WS'" class="grid">
        <div class="unit whole">
            <div class="grid">
                <div class="unit whole">
                    <div class="panel">
                        <h3>Pupils and groups</h3>
                        <p>View and manage pupils and groups.</p>
                        <RouterLink class="btn" to="/teacher/view-pupils">View pupils</RouterLink>
                        <RouterLink class="btn" to="/teacher/groups">Manage groups</RouterLink>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <v-dialog v-model="showEmailDialog" persistent width="700px">
        <v-card>
            <v-card-title class="px-6 mt-3">Email Address</v-card-title>
            <v-card-text>
                There is no email address associated with your Giglets account. Please add an email address to your
                account in case you ever need to reset your password.
            </v-card-text>
            <v-card-text>
                <v-text-field
                    ref="emailField"
                    v-model="emailAddress"
                    :rules="[checkEmail]"
                    label="Email address"
                ></v-text-field>
                <v-checkbox
                    v-model="marketing"
                    label="I would like to receive an email each term that tells me about new content and features on Giglets."
                ></v-checkbox>
            </v-card-text>
            <v-card-actions class="pt-0 mt-3">
                <v-spacer></v-spacer>
                <v-btn variant="text" class="text-disabled" @click="cancelEmail">Cancel</v-btn>
                <v-btn color="primary" variant="text" @click="saveEmail">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { GigletsBookV1 } from '@ilteducation/content-registry-types';

definePage({ meta: { area: 'home' } });

const router = useRouter();
const contentSearchStore = useContentSearchStore();

const store = useTeacherStore();
store.fetchTeacher();
store.fetchSchool();
store.fetchTaskStats();
store.fetchFeaturedBooks();
store.fetchNotices();
const { teacher, school, taskStats, featuredBooks, notices } = storeToRefs(store);

const showEmailDialog = ref(false);
const emailAddress = ref('');
const marketing = ref(false);
const emailField = ref();

const featuredContentRegistryBooks = ref<GigletsBookV1[]>([]);
watchEffect(async () => {
    const bookIds = featuredBooks.value.map((id) => id.toString());
    const contentSearchResult = await contentSearchStore.getBooks(bookIds);
    // Order books according to the order in the featuredBooks array
    for (let i = 0; i < bookIds.length; i++) {
        const book = contentSearchResult.find((book) => book.bookId === bookIds[i]);
        if (book) {
            featuredContentRegistryBooks.value[i] = book;
        }
    }
});

function gotoBook(book: GigletsBookV1) {
    router.push(`/teacher/book/${book.bookId}/about`);
}

watchEffect(() => {
    showEmailDialog.value = teacher.value
        ? !teacher.value.email && !window.sessionStorage.getItem('giglets.teacher.seenEmailReminder')
        : false;
});

function checkEmail(value: string) {
    // see: https://html.spec.whatwg.org/multipage/input.html#e-mail-state-(type%3Demail)
    return (
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
            value,
        ) || 'Invalid email address'
    );
}

async function saveEmail() {
    const errors = await emailField.value.validate();
    if (errors.length === 0) {
        await store.updateTeacher({ email: emailAddress.value, marketing_opt_in: marketing.value });
        showEmailDialog.value = false;
    }
}

function cancelEmail() {
    window.sessionStorage.setItem('giglets.teacher.seenEmailReminder', 'true');
    showEmailDialog.value = false;
}
</script>

<style scoped lang="scss">
:deep(.v-carousel .v-btn) {
    &.v-window__left,
    &.v-window__right {
        border-radius: 50% !important;
        width: 40px !important;
        height: 40px !important;
    }
    .v-btn__content {
        padding-top: revert;
    }
}
</style>
